import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  CircleX,
  Copy,
  Globe,
  TriangleAlert,
} from "lucide-react";
import { ReactNode, useState } from "react";
import {
  ConversationSource,
  EnhancementQueryAnalysis,
  WorkflowTaskResult,
  WorkflowTraceNode,
  WorkflowViolation,
} from "../../../types/conversation.model";

export const ExpandableSection = ({
  title,
  children,
  defaultExpanded = false,
}: {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="mt-4 first:mt-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-md hover:bg-surface-hover -mx-1 flex w-full items-center justify-between rounded-sm px-1 py-2 font-semibold text-high-emphasis"
      >
        <span>{title}</span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-medium-emphasis" />
        ) : (
          <ChevronDown className="h-4 w-4 text-medium-emphasis" />
        )}
      </button>
      {isExpanded && <div className="mt-2 text-sm text-medium-emphasis">{children}</div>}
    </div>
  );
};

export const RetrievalItem = ({
  query,
  index,
  title,
}: {
  query: string;
  index?: number;
  title?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-sm border">
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="hover:bg-surface-hover flex w-full items-center justify-between p-2 text-sm font-semibold text-high-emphasis transition-colors"
      >
        <div className="flex items-center gap-2">
          <CircleCheck className="h-4 w-4 text-green-500" />
          <span>{title || `Sub Query ${(index || 0) + 1}`}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-medium-emphasis" />
        ) : (
          <ChevronDown className="h-4 w-4 text-medium-emphasis" />
        )}
      </button>
      {isExpanded && (
        <div className="bg-surface-hover/20 border-t p-2 text-sm text-medium-emphasis">{query}</div>
      )}
    </div>
  );
};

export const SourceItem = ({ index, content }: { index: number; content: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-sm border">
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="hover:bg-surface-hover flex w-full items-center justify-between p-2 text-sm font-semibold text-high-emphasis transition-colors"
      >
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-high-emphasis" />
          <span>Source {index}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-medium-emphasis" />
        ) : (
          <ChevronDown className="h-4 w-4 text-medium-emphasis" />
        )}
      </button>
      {isExpanded && (
        <div className="bg-surface-hover/20 border-t p-2 text-sm text-medium-emphasis">
          {content}
        </div>
      )}
    </div>
  );
};

const ContentBox = ({ content }: { content: string }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    if (!content) return;
    navigator.clipboard.writeText(content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="group relative">
      <div
        className="max-h-[200px] overflow-y-auto rounded-lg border border-border bg-muted/30 p-3 pr-8 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgb(156 163 175) transparent",
        }}
      >
        <pre className="select-text whitespace-pre-wrap break-words text-sm text-medium-emphasis">
          {content}
        </pre>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleCopy();
        }}
        className="hover:bg-surface-hover absolute right-2 top-2 rounded-md p-1.5 text-medium-emphasis opacity-0 transition-opacity hover:text-high-emphasis group-hover:opacity-100"
        title="Copy"
      >
        {isCopied ? (
          <Check className="mr-2h-4 w-4 text-green-500" />
        ) : (
          <Copy className="mr-2 h-4 w-4" />
        )}
      </button>
    </div>
  );
};

export const ToolItem = ({ tool }: { tool: WorkflowTaskResult }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const duration = tool.completed_at && tool.started_at ? tool.completed_at - tool.started_at : 0;
  const toolName = tool.tool_name || tool.action;
  const formatJson = (data: unknown) => {
    if (data === null || data === undefined) return "";
    try {
      if (typeof data === "string") return data;
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  return (
    <div className="rounded-sm border">
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="hover:bg-surface-hover flex w-full items-center justify-between p-2 text-sm font-semibold text-high-emphasis transition-colors"
      >
        <div className="flex items-center gap-2">
          {tool.success ? (
            <CircleCheck className="h-4 w-4 text-green-500" />
          ) : (
            <CircleX className="h-4 w-4 text-red-500" />
          )}
          <span>{toolName.replaceAll("_", " ")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs italic text-medium-emphasis">{duration.toFixed(2)}s</span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-medium-emphasis" />
          ) : (
            <ChevronDown className="h-4 w-4 text-medium-emphasis" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="flex flex-col gap-4 border-t p-3">
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase text-high-emphasis">Input</h4>
            <ContentBox content={formatJson(tool.tool_params)} />
          </div>
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase text-high-emphasis">Output</h4>
            <ContentBox content={formatJson(tool.tool_output)} />
          </div>
        </div>
      )}
    </div>
  );
};

export const GuardrailsSection = ({ guardrails }: { guardrails: WorkflowViolation[] }) => {
  if (guardrails.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-high-emphasis">Guardrails</h3>
        <TriangleAlert className="h-4 w-4 text-high-emphasis" />
      </div>
      <div className="flex flex-col gap-2">
        {guardrails.map((violation, idx) => (
          <div key={idx} className="flex flex-col gap-1 rounded-sm text-sm text-medium-emphasis">
            <div className="flex gap-1">
              <span className="font-semibold text-high-emphasis">Applied Rule Type:</span>
              <span>{violation.rule_type}</span>
            </div>
            <div className="flex gap-1">
              <span className="font-semibold text-high-emphasis">Applied Rule Name:</span>
              <span>{violation.rule_name}</span>
            </div>
            <div className="flex gap-1">
              <span className="font-semibold text-high-emphasis">Violation Message:</span>
              <span>{violation.violation_message}</span>
            </div>
            <div className="flex gap-1">
              <span className="font-semibold text-high-emphasis">Severity:</span>
              <span>{violation.severity}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WorkflowStepsSection = ({ nodes }: { nodes: WorkflowTraceNode[] }) => {
  if (nodes.length === 0) return null;
  return (
    <ExpandableSection title="Workflow Steps" defaultExpanded={true}>
      <div className="flex flex-col gap-2">
        {nodes.map((node, idx) => {
          const duration = node.duration ?? "0s";
          return (
            <div key={idx} className="flex flex-row justify-between rounded-sm border p-2">
              <div className="flex items-center gap-2">
                {node.success ? (
                  <CircleCheck className="h-4 w-4 text-green-500" />
                ) : (
                  <CircleX className="h-4 w-4 text-red-500" />
                )}
                <span className="text-sm font-medium capitalize">
                  {node.name?.replaceAll("_", " ")}
                </span>
              </div>
              <span className="text-sm italic text-medium-emphasis">
                {typeof duration === "number" ? duration.toFixed(3) : duration}s
              </span>
            </div>
          );
        })}
      </div>
    </ExpandableSection>
  );
};

export const ToolsSection = ({ tools }: { tools: WorkflowTaskResult[] }) => {
  if (tools.length === 0) return null;
  return (
    <ExpandableSection title="Tools" defaultExpanded={true}>
      <div className="flex flex-col gap-2">
        {tools.map((tool, idx) => (
          <ToolItem key={idx} tool={tool} />
        ))}
      </div>
    </ExpandableSection>
  );
};

export const RetrievalSection = ({
  retrievals,
  initialQuery,
  documents,
}: {
  retrievals: EnhancementQueryAnalysis[];
  initialQuery?: string;
  documents: ConversationSource[];
}) => {
  if (retrievals.length === 0 && !initialQuery && documents.length === 0) return null;

  return (
    <ExpandableSection title="Retrieval" defaultExpanded={true}>
      <div className="flex flex-col gap-2">
        {initialQuery && <RetrievalItem title="Initial Query" query={initialQuery} />}
        {retrievals
          .flatMap((item) => item.enhanced_queries || [])
          .map((query, idx) => (
            <RetrievalItem key={idx} query={query} index={idx} />
          ))}

        {documents.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-2 text-sm font-semibold text-high-emphasis">Final Sources</h4>
            <div className="flex flex-col gap-2">
              {documents.map((item, idx) => (
                <SourceItem key={idx} index={idx + 1} content={item.page_content!} />
              ))}
            </div>
          </div>
        )}
      </div>
    </ExpandableSection>
  );
};
